'use client'

import { createContext, useContext, ReactNode } from 'react'
import { useUser, useClerk } from '@clerk/nextjs'

// Adapter interface to match old Netlify Auth
interface NetlifyUserAdapter {
  id: string
  email: string
  app_metadata: {
    roles: string[]
  }
  user_metadata: {
    full_name?: string
    avatar_url?: string
  }
}

interface AuthContextType {
  user: NetlifyUserAdapter | null
  loading: boolean
  isAdmin: boolean
  isEditor: boolean
  role: 'admin' | 'editor' | 'public'
  login: () => void
  logout: () => void
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  isAdmin: false,
  isEditor: false,
  role: 'public',
  login: () => { },
  logout: () => { }
})

export function useAuth() {
  return useContext(AuthContext)
}

interface AuthProviderProps {
  children: ReactNode // In the layout this might be empty if we just use it for context, but usually it wraps.
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { isLoaded, isSignedIn, user } = useUser()
  const { openSignIn, signOut } = useClerk()

  // Map Clerk user to Netlify shape for backward compatibility
  const mappedUser: NetlifyUserAdapter | null = isSignedIn && user ? {
    id: user.id,
    email: user.primaryEmailAddress?.emailAddress || '',
    app_metadata: {
      roles: (user.publicMetadata.roles as string[]) || []
    },
    user_metadata: {
      full_name: user.fullName || '',
      avatar_url: user.imageUrl
    }
  } : null

  // Determine roles from Clerk metadata
  // IMPORTANT: You must set publicMetadata.roles = ['admin'] in Clerk Dashboard for your user
  const roles = (user?.publicMetadata?.roles as string[]) || []
  const isAdmin = roles.includes('admin')
  const isEditor = roles.includes('editor')

  const value: AuthContextType = {
    user: mappedUser,
    loading: !isLoaded,
    isAdmin,
    isEditor,
    role: isAdmin ? 'admin' : isEditor ? 'editor' : 'public',
    login: () => openSignIn(),
    logout: () => signOut()
  }

  // If used as a wrapper, render children. 
  // Note: In layout.tsx, I wrapped children *inside* it? No, wait.
  // In previous layout: <AuthProvider> children </AuthProvider>
  // So yes, verify we render {children}
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
